import React, { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { differenceInCalendarDays } from "date-fns";
import {
  Typography,
  Box,
  Divider,
  Stack,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Paper,
  Chip,
  IconButton,
  alpha,
  useTheme,
  Grid,
  Card,
  Avatar,
  CircularProgress
} from "@mui/material";
import {
  Email as EmailIcon,
  Phone as PhoneIcon,
  EventNote as NoteIcon,
  History as HistoryIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  AccountCircle as UserIcon,
  QuestionAnswer as QaIcon,
  Feedback as FeedbackIcon
} from "@mui/icons-material";
import { getSubscriptionLeadById, updateSubscriptionLead} from "../api/subscriptionLead";
import { getAllQuestions } from "../api/question";
import { fetchAllAnsByLeadId, updateAnswersBulk } from "../api/answer";

export default function SubscriptionLead() {
  const theme = useTheme();
  const [searchParams] = useSearchParams();
  const id = searchParams.get("id");

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [leadsData, setLeadsData] = useState({});
  const [questions, setQuestions] = useState([]);
  const [qadialogOpen, setQaDialogOpen] = useState(false);
  const [updateLeadDialogOpen, setUpdateLeadDialogOpen] = useState(false);
  const [answers, setAnswers] = useState([]);
  const [status, setStatus] = useState("");
  const [notes, setNotes] = useState("");
  const [followUpDate, setFollowUpDate] = useState(null);

  const getData = async () => {
    setLoading(true);
    try {
      const data = await getSubscriptionLeadById(id);
      if (data.status) {
        setLeadsData(data.data);
      }
    } catch (error) {
      setError("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  const getQuestions = async () => {
    try {
      const data = await getAllQuestions();
      if (data.status) {
        setQuestions(data.data);
      }
    } catch (error) {}
  };

  const getAns = async () => {
    try {
      const data = await fetchAllAnsByLeadId(id);
      if (data.status) {
        setAnswers(data.data);
      }
    } catch (error) {}
  };

  useEffect(() => {
    getData();
    getQuestions();
    getAns();
  }, [id]);

  const handleOpenQaDialog = () => {
    setQaDialogOpen(true);
  };

  const handleCloseQaDialog = () => {
    setQaDialogOpen(false);
  };

  const handleOpenUpdateLeadDialog = () => {
    setUpdateLeadDialogOpen(true);
    setStatus(leadsData.status);
    setNotes(leadsData.notes || "");
    setFollowUpDate(
      leadsData.followUpDate ? leadsData.followUpDate.split("T")[0] : null
    );
  };

  const handleCloseUpdateLeadDialog = () => {
    setUpdateLeadDialogOpen(false);
  };

  const handleAnswerChange = (questionId, answer, leadId) => {
    setAnswers((prevAnswers) => {
      const existingEntryIndex = prevAnswers.findIndex(
        (ans) => ans.questionId === questionId
      );

      if (existingEntryIndex !== -1) {
        const updatedAnswers = [...prevAnswers];
        updatedAnswers[existingEntryIndex] = {
          ...updatedAnswers[existingEntryIndex],
          answer,
        };
        return updatedAnswers;
      } else {
        return [
          ...prevAnswers,
          { answer, questionId, leadId },
        ];
      }
    });
  };

  const getAnsByQusId = (qusId) => {
    const foundAnswer = answers.find((ans) => ans.questionId === qusId);
    return foundAnswer ? foundAnswer.answer : "";
  };

  const handleQaUpdate = async () => {
    const res = await updateAnswersBulk(answers);
    if (res.status) {
      handleCloseQaDialog();
      getAns();
    }
  };

  const handleLeadUpdate = async () => {
    const updatePayload = {};
    if (status) updatePayload.status = status;
    if (notes) updatePayload.notes = notes;
    if (followUpDate) updatePayload.followUpDate = followUpDate;

    const response = await updateSubscriptionLead(leadsData._id, updatePayload);
    if (response.status) {
      getData();
    }
    handleCloseUpdateLeadDialog();
  };

  const handleLeadDelete = async () => {
     if (window.confirm("Are you sure you want to delete this subscription lead?")) {
        console.log("Delete logic here");
     }
  };

  if (loading && !leadsData._id) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 4, textAlign: 'center' }}>
        <Typography color="error">{error}</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 1200, mx: "auto", p: { xs: 2, md: 3 } }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 4 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 900, mb: 0.5 }}>
            Subscription Lead
          </Typography>
          <Typography variant="body2" color="text.secondary">
            User subscription details and follow-up activities
          </Typography>
        </Box>
        <Stack direction="row" spacing={1}>
           <Button
            variant="outlined"
            size="small"
            startIcon={<DeleteIcon />}
            color="error"
            onClick={handleLeadDelete}
            sx={{ borderRadius: 2 }}
          >
            Delete
          </Button>
          <Button
            variant="contained"
            size="small"
            startIcon={<EditIcon />}
            onClick={handleOpenUpdateLeadDialog}
            sx={{ borderRadius: 2 }}
          >
            Update
          </Button>
        </Stack>
      </Box>

      <Grid container spacing={3}>
        {/* Profile Card */}
        <Grid item xs={12} md={4}>
          <Card elevation={0} sx={{ p: 3, border: '1px solid', borderColor: 'divider', borderRadius: 1.5, height: '100%' }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 3 }}>
              <Avatar sx={{ width: 80, height: 80, bgcolor: alpha(theme.palette.secondary.main, 0.1), color: 'secondary.main', mb: 2 }}>
                <UserIcon sx={{ fontSize: '3rem' }} />
              </Avatar>
              <Typography variant="h5" sx={{ fontWeight: 800 }}>{leadsData.subscription?.name || "N/A"}</Typography>
              <Chip 
                label={leadsData.status?.toUpperCase() || "NEW"} 
                color="secondary" 
                size="small" 
                sx={{ mt: 1, fontWeight: 700, borderRadius: 1.5, px: 1 }} 
              />
            </Box>
            
            <Divider sx={{ mb: 2.5 }} />
            
            <Stack spacing={2.5}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Avatar sx={{ bgcolor: alpha(theme.palette.primary.main, 0.05), color: 'primary.main', width: 32, height: 32 }}>
                   <EmailIcon fontSize="small" />
                </Avatar>
                <Box>
                  <Typography variant="caption" color="text.disabled" sx={{ fontWeight: 700, display: 'block', lineHeight: 1 }}>EMAIL ADDRESS</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>{leadsData.subscription?.email || "N/A"}</Typography>
                </Box>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Avatar sx={{ bgcolor: alpha(theme.palette.primary.main, 0.05), color: 'primary.main', width: 32, height: 32 }}>
                   <PhoneIcon fontSize="small" />
                </Avatar>
                <Box>
                  <Typography variant="caption" color="text.disabled" sx={{ fontWeight: 700, display: 'block', lineHeight: 1 }}>PHONE NUMBER</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>{leadsData.subscription?.phone || "N/A"}</Typography>
                </Box>
              </Box>
            </Stack>
          </Card>
        </Grid>

        {/* Lead Stats & Actions */}
        <Grid item xs={12} md={8}>
          <Grid container spacing={3}>
            {/* Communication */}
            <Grid item xs={12} sm={6}>
              <Card elevation={0} sx={{ p: 2.5, border: '1px solid', borderColor: 'divider', borderRadius: 1.5, bgcolor: alpha(theme.palette.primary.main, 0.02) }}>
                <Typography variant="subtitle1" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1, fontWeight: 700 }}>
                  <NoteIcon color="primary" fontSize="small" /> Communication
                </Typography>
                <Stack spacing={1.5}>
                  <Button variant="outlined" fullWidth startIcon={<EmailIcon />} sx={{ justifyContent: 'flex-start', borderRadius: 2 }}>
                    Send Reminder Email
                  </Button>
                  <Button variant="outlined" fullWidth startIcon={<PhoneIcon />} sx={{ justifyContent: 'flex-start', borderRadius: 2 }}>
                    Call Subscriber
                  </Button>
                </Stack>
              </Card>
            </Grid>
            
            {/* Feedback & Info */}
            <Grid item xs={12} sm={6}>
              <Card elevation={0} sx={{ p: 2.5, border: '1px solid', borderColor: 'divider', borderRadius: 1.5 }}>
                <Typography variant="subtitle1" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1, fontWeight: 700 }}>
                  <FeedbackIcon color="primary" fontSize="small" /> User Feedback
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Collect user insights to improve retention strategies.
                </Typography>
                <Button 
                  variant="contained" 
                  fullWidth 
                  startIcon={<QaIcon />} 
                  onClick={handleOpenQaDialog}
                  sx={{ borderRadius: 2 }}
                >
                  Retention Form
                </Button>
              </Card>
            </Grid>

            {/* Note Section */}
            <Grid item xs={12}>
              <Card elevation={0} sx={{ p: 3, border: '1px solid', borderColor: 'divider', borderRadius: 1.5 }}>
                 <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                  <Typography variant="subtitle1" sx={{ display: 'flex', alignItems: 'center', gap: 1, fontWeight: 700 }}>
                    <HistoryIcon color="primary" fontSize="small" /> Internal Remarks
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {leadsData.updatedAt ? `Last update: ${differenceInCalendarDays(new Date(), new Date(leadsData.updatedAt))} days ago` : "No updates"}
                  </Typography>
                </Box>
                <Paper variant="outlined" sx={{ p: 2, bgcolor: alpha(theme.palette.primary.main, 0.02), borderStyle: 'dashed', borderRadius: 3 }}>
                    <Typography variant="body2" color="text.secondary">
                      {leadsData.notes || "No internal notes recorded for this subscriber lead yet."}
                    </Typography>
                </Paper>
              </Card>
            </Grid>
          </Grid>
        </Grid>
      </Grid>

      {/* Dialog for Q&A Retention Form */}
      <Dialog 
        open={qadialogOpen} 
        onClose={handleCloseQaDialog} 
        fullWidth
        PaperProps={{ sx: { borderRadius: 1.5, bgcolor: '#f8fafc' } }}
      >
        <DialogTitle sx={{ fontWeight: 800, px: 3, pt: 3 }}>
          <Stack direction="row" alignItems="center" spacing={1}>
             <QaIcon color="primary" />
             <Typography variant="h6" display="inline">Retention Feedback</Typography>
          </Stack>
        </DialogTitle>
        <DialogContent sx={{ px: 3 }}>
          <Stack spacing={3} sx={{ mt: 1 }}>
            {questions.map((question) => (
              <Box key={question._id}>
                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 700, color: 'text.primary' }}>
                  {question.question}
                </Typography>
                <TextField
                  fullWidth
                  multiline
                  rows={2}
                  variant="outlined"
                  placeholder="Enter details..."
                  value={getAnsByQusId(question._id)}
                  onChange={(e) => handleAnswerChange(question._id, e.target.value, leadsData._id)}
                  sx={{ bgcolor: 'white' }}
                />
              </Box>
            ))}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={handleCloseQaDialog} color="inherit">Cancel</Button>
          <Button onClick={handleQaUpdate} variant="contained" sx={{ px: 4, borderRadius: 2 }}>Save Responses</Button>
        </DialogActions>
      </Dialog>

      {/* Dialog for updating status and notes */}
      <Dialog 
        open={updateLeadDialogOpen} 
        onClose={handleCloseUpdateLeadDialog}
        PaperProps={{ sx: { borderRadius: 1.5, width: '100%', maxWidth: 450 } }}
      >
        <DialogTitle sx={{ fontWeight: 800 }}>Update Status</DialogTitle>
        <DialogContent>
          <Stack spacing={3} sx={{ pt: 1 }}>
            <FormControl fullWidth>
              <InputLabel>Lead Status</InputLabel>
              <Select
                value={status}
                label="Lead Status"
                onChange={(e) => setStatus(e.target.value)}
              >
                <MenuItem value="asigned">Assigned</MenuItem>
                <MenuItem value="pending">Pending</MenuItem>
                <MenuItem value="contacted">Contacted</MenuItem>
                <MenuItem value="follow">Follow-up</MenuItem>
                <MenuItem value="completed">Completed</MenuItem>
                <MenuItem value="denied">Denied</MenuItem>
              </Select>
            </FormControl>
            
            {status === "follow" && (
              <TextField
                label="Follow-up Date"
                type="date"
                value={followUpDate}
                onChange={(e) => setFollowUpDate(e.target.value)}
                fullWidth
                InputLabelProps={{ shrink: true }}
              />
            )}
            
            <TextField
              label="Interaction Notes"
              multiline
              rows={4}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              fullWidth
              placeholder="Record details of your discussion with the user..."
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleCloseUpdateLeadDialog} color="inherit">Cancel</Button>
          <Button onClick={handleLeadUpdate} variant="contained" sx={{ px: 4, borderRadius: 2 }}>Update Lead</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

